<script setup lang="ts">
import { watch } from 'vue';
import { useForm } from 'vee-validate';
import { erhlRetiredFinancialChangesSchema } from '@/validation';
import type { RetirementDetails } from '@/types';
import { defaultRetirementDetails } from '@/helpers/builder';

const props = withDefaults(
  defineProps<{
    applicantId: string;
    modelValue: RetirementDetails | null;
  }>(),
  {},
);

const emit = defineEmits<{
  (e: 'update:modelValue', v: RetirementDetails): void;
}>();

const { meta, values, submitCount, handleSubmit, resetForm } = useForm<RetirementDetails>({
  validationSchema: erhlRetiredFinancialChangesSchema,
  validateOnMount: false,
});

watch(
  () => props.modelValue,
  (data) => {
    resetForm({ values: data ?? defaultRetirementDetails() });
  },
  { immediate: true, deep: true },
);

/**
 * Validates and emits the retirement details to the parent.
 * Returns true on success, false on validation failure.
 */
const update = handleSubmit(
  (validated) => {
    emit('update:modelValue', {
      ...defaultRetirementDetails(),
      financialChangesDuringLoan: validated.financialChangesDuringLoan ?? false,
      financialChangesDuringLoanDescription: validated.financialChangesDuringLoanDescription ?? '',
    });
    return true;
  },
  () => false,
);

/** Resets the form back to prop values. */
const cancel = () => resetForm();

defineExpose({ update, cancel });
</script>

<template>
  <div>
    <div class="form-control my-6">
      <BxField v-slot="{ field }" name="financialChangesDuringLoan">
        <BxRadio
          v-bind="field"
          :id="applicantId + '-financial-changes-during-loan'"
          label="Do you expect any changes to your financial situation over the life of this loan?"
          label-type="default"
          boolean
        />
      </BxField>
      <Transition name="fade">
        <div v-if="values.financialChangesDuringLoan">
          <BxField v-slot="{ field }" name="financialChangesDuringLoanDescription">
            <BxTextarea
              v-bind="field"
              :id="applicantId + '-financial-changes-during-loan-desc'"
              label="Describe these changes"
              label-type="floating"
              class="mt-4"
            />
          </BxField>
        </div>
      </Transition>
    </div>

    <BxErrorPanel v-show="!meta.valid && submitCount > 0" class="mt-6">
      Please review the error messages above.
    </BxErrorPanel>
  </div>
</template>
